const SAVED_VIEW_SETTING_KEYS = [
    'gridSize',
    'gridColor',
    'viewMode',
    'masonrySizing',
    'showHeadline',
    'showRating',
    'showFilenames',
    'showIcons',
    'autoPlay',
]

// `dark` follows the app theme: the grid defaults to a dark surface so a dark UI doesn't frame a
// white grid. Organization and asset-group settings still win, since grid color is their choice.
const buildViewSettingsDefaults = ({mobile = false, dark = false, organizationSettings = {}} = {}) => ({
    gridSize: mobile ? 'xl' : 'm',
    gridColor: dark ? '#000000' : '#ffffff',
    viewMode: 'grid',
    showFilenames: false,
    showHeadline: false,
    autoPlay: false,
    convertSpacesToOr: false,
    convertCommasToOr: false,
    showIcons: true,
    showRating: false,
    colorLabel: null,
    ...(organizationSettings || {}),
    masonrySizing: organizationSettings?.masonrySizing === 'width' ? 'width' : 'height',
})

const selectSavedViewSettings = (settings = {}) => Object.fromEntries(
    SAVED_VIEW_SETTING_KEYS.map(key => [key, settings[key]])
)

// The viewer's grid color choice is remembered per theme (black grid in dark mode, grey in
// light, say). Only the choice for the active theme participates in resolution; when none was
// made for that theme the key is omitted so the theme default (or org/asset-group value) applies.
const sessionViewSettingsForTheme = (sessionSettings = {}, theme = 'light') => {
    const {gridColor: _legacyGridColor, gridColorByTheme, ...rest} = sessionSettings || {}
    const gridColor = gridColorByTheme?.[theme]

    return gridColor ? {...rest, gridColor} : rest
}

const resolveViewSettings = ({
    defaults = {},
    sessionSettings = {},
    assetGroupSettings = {},
    manual = false,
}) => {
    const resolved = {...defaults, ...sessionSettings, ...(manual ? {} : (assetGroupSettings || {}))}
    return {...resolved, masonrySizing: resolved.masonrySizing === 'width' ? 'width' : 'height'}
}

// Keys left undefined in `value` are "not set" (e.g. a theme-derived grid color the viewer never
// chose) and are neither persisted nor counted as a change.
const hasAssetGroupViewSettingsChanges = ({value, defaults = {}, assetGroupSettings = {}}) => {
    const persistedDefault = resolveViewSettings({defaults, assetGroupSettings})

    return SAVED_VIEW_SETTING_KEYS.some(key => value[key] !== undefined && value[key] !== persistedDefault[key])
}

export {
    SAVED_VIEW_SETTING_KEYS,
    buildViewSettingsDefaults,
    hasAssetGroupViewSettingsChanges,
    resolveViewSettings,
    selectSavedViewSettings,
    sessionViewSettingsForTheme,
}
